import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import swal from "sweetalert";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PageTitle from "../../../layouts/PageTitle";
import {
	cancelBooking,
	CANCELLED_STATUS_ID,
	editBooking,
	formatApiError,
	getSingleBookingDetail,
	getSingleVehicleDetail,
	getStatusDropdown,
} from "../../../../services/TransferService";
import { canEditBooking, EDIT_LOCK_HOURS, isBookableDate } from "../../../../utils/booking";

const EDITABLE_FIELDS = [
	{ label: "Passenger Name", field: "user_name", required: true },
	{ label: "Phone", field: "user_phone", type: "tel", required: true },
	{ label: "Email Address", field: "user_email", type: "email", required: true },
	{ label: "Flight Number / Cruise Details", field: "flight_number" },
	{ label: "No. of Children", field: "no_of_child", type: "number" },
	// Hidden for now - keep for future use
	{ label: "Agency Ref No", field: "agency_ref_no", hidden: true },
	{ label: "Reservation/Booking ID", field: "agency_staff", required: true },
];

const EditBookingDetails = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const bookingId = location.state?.bookingId;
	const agent = useSelector((state) => state.auth.auth) || {};

	const [booking, setBooking] = useState(null);
	const [vehicle, setVehicle] = useState(null);
	const [statusLabels, setStatusLabels] = useState({});
	const [loading, setLoading] = useState(true);
	const [cancelling, setCancelling] = useState(false);
	const [form, setForm] = useState({
		user_name: "",
		user_phone: "",
		user_email: "",
		flight_number: "",
		requirements: "",
		no_of_child: "",
		agency_ref_no: "",
		agency_staff: "",
		status: "",
		booking_date: "",
	});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!bookingId) {
			setLoading(false);
			return;
		}
		Promise.all([getSingleBookingDetail(bookingId), getStatusDropdown().catch(() => ({}))])
			.then(([detail, labels]) => {
				setBooking(detail);
				setStatusLabels(labels);
				if (detail) {
					const pickupMoment = moment(detail.booking_date, "YYYY-MM-DD HH:mm:ss");
					setForm({
						user_name: detail.user_name || "",
						user_phone: detail.user_phone || "",
						user_email: detail.user_email || "",
						flight_number: detail.flight_number || "",
						requirements: detail.requirements || "",
						no_of_child: detail.no_of_child || "",
						agency_ref_no: detail.agency_ref_no || "",
						agency_staff: detail.agency_staff || "",
						status: detail.status ?? "",
						booking_date: pickupMoment.isValid() ? pickupMoment.format("YYYY-MM-DDTHH:mm") : "",
					});
					if (detail.vehicle_id) {
						getSingleVehicleDetail(detail.vehicle_id)
							.then(setVehicle)
							.catch(() => {});
					}
				}
			})
			.finally(() => setLoading(false));
	}, [bookingId]);

	function handleChange(field) {
		return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
	}

	async function handleSubmit(e) {
		e.preventDefault();
		if (!booking || !isFormValid) return;
		setSaving(true);
		// get_single_booking_detail.php returns the primary key as `id`, but edit_booking.php
		// expects it as `booking_id` — a different field name, so it must be set explicitly.
		// form.booking_date is held in datetime-local format (YYYY-MM-DDTHH:mm) for the input;
		// convert back to the API's "YYYY-MM-DD HH:mm:ss" format before sending.
		const bookingDateMoment = moment(form.booking_date, "YYYY-MM-DDTHH:mm");
		const payload = {
			...booking,
			...form,
			booking_id: booking.id,
			booking_date: bookingDateMoment.isValid() ? bookingDateMoment.format("YYYY-MM-DD HH:mm:ss") : booking.booking_date,
		};
		try {
			await editBooking(payload);
			await swal("Saved", "Booking updated successfully", "success");
			navigate("/transfers");
		} catch (error) {
			if (error.response) {
				formatApiError(error.response.data, "Unable to update booking. Please try again.");
			} else {
				formatApiError(null, "Unable to reach the server. Please try again.");
			}
		} finally {
			setSaving(false);
		}
	}

	async function handleCancelBooking() {
		if (!booking) return;
		const confirmed = await swal({
			title: "Cancel this booking?",
			text: "This will cancel the booking. This can't be undone here.",
			icon: "warning",
			buttons: ["Keep booking", "Cancel booking"],
			dangerMode: true,
		});
		if (!confirmed) return;

		setCancelling(true);
		try {
			await cancelBooking({ booking_id: booking.id, agent_id: agent.id });
			setBooking((prev) => ({ ...prev, status: CANCELLED_STATUS_ID }));
			setForm((prev) => ({ ...prev, status: CANCELLED_STATUS_ID }));
			await swal("Cancelled", "Booking has been cancelled.", "success");
		} catch (error) {
			if (error.response) {
				formatApiError(error.response.data, "Unable to cancel booking. Please try again.");
			} else {
				formatApiError(null, "Unable to reach the server. Please try again.");
			}
		} finally {
			setCancelling(false);
		}
	}

	if (loading) {
		return <div className="text-center text-muted py-5">Loading booking...</div>;
	}

	if (!bookingId || !booking) {
		return (
			<div className="card">
				<div className="card-body text-center py-5">
					<p className="mb-3">Booking not found.</p>
					<button type="button" className="btn btn-primary" onClick={() => navigate("/transfers")}>
						Back to Transfers
					</button>
				</div>
			</div>
		);
	}

	const isCancelled = String(booking.status) === CANCELLED_STATUS_ID;
	const editable = canEditBooking(booking) && !isCancelled;
	const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
	const isFormValid =
		form.user_name.trim() !== "" &&
		form.user_phone.trim() !== "" &&
		isValidEmail(form.user_email) &&
		form.agency_staff.trim() !== "";
	const pickup = moment(booking.booking_date, "YYYY-MM-DD HH:mm:ss");
	const statusLabel = statusLabels[booking.status] || "Pending";
	const pickupMoment = moment(form.booking_date, "YYYY-MM-DDTHH:mm", true);
	const pickupDate = pickupMoment.isValid() ? pickupMoment.toDate() : null;

	return (
		<>
			<PageTitle activeMenu="Edit Booking" motherMenu="Transfers" />
			<div className="row align-items-start">
				<div className="col-lg-8">
					<div className="card">
						<div className="card-body">
							<h4 className="mb-3">Edit Passenger Details</h4>

							{isCancelled ? (
								<div className="alert alert-danger">Booking already cancelled.</div>
							) : (
								!editable && (
									<div className="alert alert-warning">
										This booking can no longer be edited — changes are only allowed more than {EDIT_LOCK_HOURS} hours
										before pickup.
									</div>
								)
							)}

							<form onSubmit={handleSubmit}>
								<div className="row g-3">
									{EDITABLE_FIELDS.filter((f) => !f.hidden).map((f) => (
										<div className="col-md-6" key={f.field}>
											<label className="form-label">
												{f.label} {f.required && <span className="text-danger">*</span>}
											</label>
											<input
												type={f.type || "text"}
												className="form-control"
												value={form[f.field]}
												onChange={handleChange(f.field)}
												disabled={!editable}
												required={f.required}
											/>
										</div>
									))}
									{/* Status field hidden for now - keep for future use
									<div className="col-md-6">
										<label className="form-label">Status</label>
										<select
											className="form-control"
											value={form.status}
											onChange={handleChange("status")}
											disabled={!editable}
										>
											{Object.keys(statusLabels).length === 0 && (
												<option value={form.status}>{statusLabels[form.status] || "Pending"}</option>
											)}
											{Object.entries(statusLabels).map(([value, label]) => (
												<option key={value} value={value}>
													{label}
												</option>
											))}
										</select>
									</div>
									*/}
									<div className="col-md-6">
										<label className="form-label">Pickup Date &amp; Time</label>
										<DatePicker
											className="form-control"
											wrapperClassName="w-100"
											selected={pickupDate}
											onChange={(date) =>
												setForm((prev) => ({
													...prev,
													booking_date: date ? moment(date).format("YYYY-MM-DDTHH:mm") : "",
												}))
											}
											showTimeSelect
											timeIntervals={15}
											timeCaption="Time"
											placeholderText="Select date & time"
											dateFormat="dd MMM yyyy, h:mm aa"
											filterDate={isBookableDate}
											disabled={!editable}
										/>
									</div>
									<div className="col-12">
										<label className="form-label">Special Requirement</label>
										<textarea
											className="form-control"
											rows={3}
											value={form.requirements}
											onChange={handleChange("requirements")}
											disabled={!editable}
										/>
									</div>
								</div>

								<div className="d-flex justify-content-between align-items-center flex-wrap mt-4" style={{ gap: "0.5rem" }}>
									<div>
										{!isCancelled && (
											<button
												type="button"
												className="btn btn-outline-danger"
												onClick={handleCancelBooking}
												disabled={cancelling}
											>
												{cancelling ? "Cancelling..." : "Cancel Booking"}
											</button>
										)}
									</div>
									<div>
										<button type="button" className="btn btn-secondary light me-2" onClick={() => navigate("/transfers")}>
											Close
										</button>
										{!isCancelled && (
											<button type="submit" className="btn btn-primary rounded-pill px-4" disabled={!editable || saving || !isFormValid}>
												{saving ? "Saving..." : "Save Changes"}
											</button>
										)}
									</div>
								</div>
							</form>
						</div>
					</div>
				</div>

				<div className="col-lg-4">
					<div className="card">
						<div className="card-body">
							<h5 className="mb-3">Booking Details</h5>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Booking Ref</span>
								<span>{booking.bookingid}</span>
							</div>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Status</span>
								<span>{statusLabel}</span>
							</div>
							<div className="d-flex justify-content-between mb-3">
								<span className="fw-500">Pickup</span>
								<span className="text-end">
									{pickup.isValid() ? pickup.format("MMM D, YYYY h:mm A") : "—"}
								</span>
							</div>

							<hr />
							<h6 className="text-primary">Route</h6>
							<div className="mb-2">
								<div className="fw-500">From</div>
								<div className="text-muted">{booking.from_address}</div>
							</div>
							<div className="mb-3">
								<div className="fw-500">To</div>
								<div className="text-muted">{booking.to_address}</div>
							</div>

							<hr />
							<h6 className="text-primary">Vehicle</h6>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Vehicle</span>
								<span>{vehicle ? `${vehicle.make || ""} ${vehicle.model || vehicle.vehicle_type_des || ""}`.trim() : `#${booking.vehicle_id}`}</span>
							</div>
							<div className="d-flex justify-content-between mb-3">
								<span className="fw-500">Passengers</span>
								<span>{booking.no_of_pax}</span>
							</div>

							<hr />
							<h6 className="text-primary">Extras</h6>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Extra Luggage</span>
								<span>{booking.extra_luggage}</span>
							</div>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Surfboard</span>
								<span>{booking.surfboard}</span>
							</div>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Baby Seats</span>
								<span>{booking.baby_seats}</span>
							</div>
							<div className="d-flex justify-content-between mb-3">
								<span className="fw-500">Baby Booster</span>
								<span>{booking.baby_booster}</span>
							</div>

							<hr />
							<h6 className="text-primary">Payment</h6>
							<div className="d-flex justify-content-between">
								<span className="fw-500">Total Paid</span>
								<span className="fs-18 fw-bold">
									{booking.currency} {Number(booking.price_total || 0).toFixed(2)}
								</span>
							</div>
							<p className="text-muted fs-13 mt-3 mb-0">
								Address, vehicle, passenger count, and extras can't be changed here — they affect pricing.
							</p>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default EditBookingDetails;
